import type SentryService from 'volta/services/sentry-service';
import application from 'volta/utils/application';

import { debug } from '@ember/debug';
import type { DocBase } from './api/jsonapi-types';
import type { EmberChangeset } from 'ember-changeset';
import type { ValidationErr } from 'validated-changeset/dist/types';

export interface IGenericError {
  error: string;
}

export interface JsonapiErrorMeta {
  customAssert?: boolean;
  isInternal?: boolean;
  [key: string]: any;
}

export interface IAPIError {
  title: string;
  detail?: { description?: string };
  meta?: JsonapiErrorMeta;
}

export interface IJSONAPIErrors extends DocBase {
  errors: IAPIError[];
}

export interface IAjaxError {
  json: IJSONAPIErrors;
}

export type TError = Error | IAPIError | IJSONAPIErrors | IAjaxError | IGenericError;

/**
 * Logs an error and sends it to Sentry
 * @param error An application error
 */
export function defaultErrorHandler(error: TError | string) {
  debug(`${error}`);

  const sentry = application?.instance?.lookup('service:sentry-service') as SentryService;
  if (sentry) {
    sentry.logError(error);
  }
}

/**
 * Extract the error title from any sort of error
 * @param {TError} error An application error
 * @returns An error message
 */
export function jsonapiErrors(error: TError | string) {
  const msg =
    (error as Error).message ??
    (error as IGenericError).error ??
    (error as IAPIError).title ??
    ((error as IJSONAPIErrors).errors?.length &&
      (error as IJSONAPIErrors).errors.map((e: IAPIError) => e.title)) ??
    ((error as IAjaxError).json?.errors?.length &&
      (error as IAjaxError).json.errors.map((e: IAPIError) => e.title)) ??
    error;
  return typeof msg === 'string' || Array.isArray(msg) ? msg : undefined;
}

export function firstApiError(error?: TError): IAPIError | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  if ('title' in error) {
    return error as IAPIError;
  }

  const jsonApiError = (error as IJSONAPIErrors).errors?.[0];
  if (jsonApiError) {
    return jsonApiError;
  }

  return (error as IAjaxError).json?.errors?.[0];
}

/**
 * Extracts the error message from a changeset
 * @param cs The changeset
 * @param field  The field to check
 * @returns  The error message for the field
 */
export const getChangesetError = (
  cs: EmberChangeset,
  field: string
): ValidationErr | ValidationErr[] | undefined => {
  return cs?.error?.[field]?.validation;
};
